//@flow

import React, { Component } from 'react';
import {
  DropdownContainer,
  DropdownTitleDiv,
  DropdownTextInput,
  ChevronSvg,
  DropdownOptionsDiv,
  ItemLi,
  Label
} from './DropdownInputStyles';
import { type ReactObjRef } from '../utilities/types';
import { InputValidationSmall } from './InputStyles';

export type Options = {
  value: string | number,
  text: string | number
};

type Props = {
  default: number,
  options: Array<Options>,
  input: {
    value: string | number,
    onChange: (value: any) => void,
    onFocus?: () => void,
    onBlur?: () => void
  },
  label?: string,
  customOnChange?: (value: any) => void,
  typeable?: boolean,
  disabled?: boolean,
  listLabel?: string,
  meta?: {
    error: string,
    touched: boolean
  },
  readOnly?: boolean,
  className?: string
};

type State = {
  open?: boolean,
  selected?: mixed,
  textInput: string,
  filteredOptions: Array<Options>,
  highlighted: number
};

class DropdownInput extends Component<Props, State> {
  static defaultProps = {
    default: 0,
    className: 'dropdown-form-input',
    disabled: false,
    options: [{ text: '', value: '' }]
  };

  constructor(props: Props) {
    super(props);
    const selected = this.findIndex(this.props.input.value);
    this.state = {
      open: false,
      selected: selected === -1 ? 0 : selected,
      textInput: '',
      filteredOptions: this.props.options,
      highlighted: 0
    };
    this.dropdownList = React.createRef();
    this.textInput = React.createRef();
  }
  dropdownList: ReactObjRef<'div'>;
  textInput: ReactObjRef<'input'>;
  closeRequest: TimeoutID;

  static getDerivedStateFromProps = (nextProps: Props) => {
    let selected;
    if (typeof nextProps.input.value === 'undefined') {
      selected = nextProps.default;
    } else {
      const index = nextProps.options.findIndex(
        val => val.value === nextProps.input.value
      );
      selected = Math.max(index, 0);
    }

    if (selected === -1) {
      selected = 0;
    }
    return { selected };
  };

  componentDidUpdate(prevProps: any, prevState: any) {
    if (prevState.selected !== this.state.selected) {
      const selected = 0;
      this.setState({ selected });
    }
  }

  findIndex = (value?: number | string) => {
    if (typeof value === 'undefined') {
      return this.props.default;
    }
    const index = this.props.options.findIndex(val => val.value === value);
    return Math.max(index, 0);
  };

  closeDropdown = () => {
    this.setState({ open: false });
    if (this.props.input.onBlur) {
      this.props.input.onBlur();
    }
    window.removeEventListener('click', this.requestClose);
  };

  requestClose = () => {
    this.closeRequest = setTimeout(() => {
      this.closeDropdown();
    }, 25);
  };

  openDropdown = () => {
    window.addEventListener('click', this.requestClose);
    const highlighted = this.findIndex(this.props.input.value);
    this.setState({
      open: !this.state.open,
      highlighted
    });
    if (this.props.input.onFocus) {
      this.props.input.onFocus();
    }
    if (this.props.typeable) {
      this.setState({
        textInput: this.props.options[highlighted].text.toString(),
        filteredOptions: this.props.options
      });
      setTimeout(() => {
        const textInput = this.textInput.current;
        if (textInput) {
          textInput.focus();
          textInput.setSelectionRange(
            this.state.textInput.length,
            this.state.textInput.length
          );
          textInput.addEventListener('keydown', this.handleTypeableSelect);
        }
      }, 10);
    }
  };

  handleTitleClick = (event: SyntheticMouseEvent<HTMLDivElement>) => {
    if (this.props.disabled || this.props.readOnly) {
      return;
    }
    if (!this.state.open) {
      // setTimeout(() => {
      this.openDropdown();
      // }, 0);
    } else if (!this.props.typeable) {
      this.closeDropdown();
    }
    event.stopPropagation();
    event.preventDefault();
  };

  handleFocus = () => {
    if (this.props.disabled) {
      return;
    }
    if (this.closeRequest) {
      clearTimeout(this.closeRequest);
    }

    if (!this.state.open && this.props.typeable) {
      setTimeout(() => {
        this.openDropdown();
      }, 2);
    }
  };

  handleChange = (value: any) => {
    this.closeDropdown();
    this.props.input.onChange(value);

    if (this.props.customOnChange) {
      this.props.customOnChange(value);
    }
  };

  handleTextInput = (event: any) => {
    const val = event.target.value.toString();
    const filteredOptions = this.props.options.filter(option =>
      option.text
        .toString()
        .toLowerCase()
        .startsWith(val.toLowerCase())
    );
    if (!filteredOptions.length) {
      filteredOptions.push({ text: '', value: '' });
    }
    const highlighted = 0;
    this.setState({ textInput: val, filteredOptions, highlighted });
    if (this.dropdownList.current) {
      this.dropdownList.current.scrollTop = 0;
    }
  };

  handleTypeableSelect = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.handleChange(
        (this.state.filteredOptions[this.state.highlighted] || {}).value
      );
    } else if (event.keyCode === 40) {
      event.preventDefault();
      const highlighted = Math.min(
        this.state.highlighted + 1,
        this.state.filteredOptions.length - 1
      );
      this.setHighlighted(highlighted);
    } else if (event.keyCode === 38) {
      event.preventDefault();
      const highlighted = Math.max(this.state.highlighted - 1, 0);
      this.setHighlighted(highlighted);
    }
  };

  setHighlighted = (highlighted: number) => {
    this.setState({ highlighted });
    this.handleScroll(highlighted);
  };

  handleScroll = (index: number) => {
    const list = this.dropdownList.current;
    if (!list) {
      return;
    }
    // $FlowFixMe force item to me HTMLDivElement to allow access to below properties
    const item: HTMLDivElement = list.childNodes[0].childNodes[index];
    if (
      item.offsetTop >
      list.scrollTop + list.offsetHeight - item.offsetHeight - 10
    ) {
      list.scrollTop =
        item.offsetTop - (list.offsetHeight - 10 - item.offsetHeight);
    } else if (item.offsetTop < list.scrollTop + 10) {
      list.scrollTop = item.offsetTop - 10;
    }
  };

  render() {
    let options = this.props.options;
    if (this.props.typeable) {
      options = this.state.filteredOptions;
    }
    const selected = this.findIndex(this.props.input.value);

    const floatingLabel =
      (this.props.options.length && this.props.options[selected].text) ||
      (this.props.typeable && this.state.open);
    const selectedOption = this.props.options.length
      ? this.props.options[selected]
      : {};
    return (
      <DropdownContainer
        className={this.props.className}
        onClick={this.handleTitleClick}
        onFocus={this.handleFocus}
        onBlur={this.props.typeable ? undefined : this.requestClose}
        tabIndex={this.state.open ? '-1' : '0'}
        disabled={this.props.disabled}
        noLabel={!this.props.label}
        readOnly={this.props.readOnly}
      >
        {this.props.label && (
          <Label
            meta={this.props.meta}
            floatingLabel={floatingLabel}
            disabled={this.props.disabled}
          >
            {this.props.label}
          </Label>
        )}
        <DropdownTitleDiv
          open={this.state.open}
          selectedOptionValue={selectedOption.value}
          meta={this.props.meta}
          readOnly={this.props.readOnly}
        >
          {this.props.typeable && this.state.open ? (
            <DropdownTextInput
              type="text"
              ref={this.textInput}
              value={this.state.textInput}
              onChange={this.handleTextInput}
              onBlur={this.props.typeable ? this.requestClose : undefined}
              autocomplete="off"
            />
          ) : (
            selectedOption.text
          )}
        </DropdownTitleDiv>
        <ChevronSvg
          readOnly={this.props.readOnly}
          styled={{ noLabel: !this.props.label }}
          meta={this.props.meta}
        />
        <DropdownOptionsDiv open={this.state.open} ref={this.dropdownList}>
          <ul>
            {options.map((option, i) => (
              <ItemLi
                key={`${option.value}${option.text}`}
                onClick={() => this.handleChange(option.value)}
                index={i}
                highlighted={this.state.highlighted}
              >
                {option.text || this.props.listLabel || this.props.label}
              </ItemLi>
            ))}
          </ul>
        </DropdownOptionsDiv>
        <InputValidationSmall meta={this.props.meta}>
          {this.props.meta && this.props.meta.error}
        </InputValidationSmall>
      </DropdownContainer>
    );
  }
}

export default DropdownInput;
